import {
  OnboardingSavePublicTransportDto,
  OnboardingSaveHighwaysDto,
  OnboardingSaveParkingDto,
  CompanyServicesResponse,
  CompanyPolicyResponse,
  ServiceType,
} from '@interfaces/models';
import {
  formatPublicTransportConfiguration,
  formatHighwaysConfiguration,
  formatParkingConfiguration,
} from '@shared/components';
import { useEmployeesProfile, useGetGeneralConfig } from '@pages/employees-profile/hooks';
import { updateEmployeeProfile } from '@api/company-policy';
import { useGetCompanyPolicyData } from '@shared/hooks';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';

export const useEmployeesProfileServicesData = (data?: CompanyPolicyResponse) => {
  const { refetchEmployeeProfile } = useEmployeesProfile(data?.id);

  const { data: companyPolicyData, isLoading: isQueryLoading } = useGetCompanyPolicyData();
  const { generalConfigData, isGeneralConfigLoading } = useGetGeneralConfig();

  const availableServices = useMemo(() => {
    return generalConfigData?.availableServices.map((service) => service.type) || [];
  }, [generalConfigData]);

  const lastServiceEnabled: ServiceType | null = useMemo(() => {
    const services = {
      [ServiceType.PUBLIC_TRANSPORT]:
        !!data?.services.publicTransportConfiguration &&
        availableServices.includes(ServiceType.PUBLIC_TRANSPORT),
      [ServiceType.HIGHWAYS]:
        !!data?.services.highwaysConfiguration && availableServices.includes(ServiceType.HIGHWAYS),
      [ServiceType.PARKINGS]:
        !!data?.services.parkingConfiguration && availableServices.includes(ServiceType.PARKINGS),
    };

    const hasOnlyOneServiceEnabled = Object.values(services).filter(Boolean).length === 1;
    if (hasOnlyOneServiceEnabled)
      return Object.keys(services).find((key) => services[key as ServiceType]) as ServiceType;
    return null;
  }, [data, availableServices]);

  const saveCompanyPolicyMutation = useMutation({
    mutationFn: (services: CompanyServicesResponse) => {
      const payload = { ...data!, services };
      return updateEmployeeProfile(data!.id, payload);
    },
    onSuccess: () => {
      refetchEmployeeProfile();
    },
  });

  const saveCompanyPolicyConfiguration = ({
    publicTransportConfiguration,
    parkingConfiguration,
    highwaysConfiguration,
  }: Partial<{
    publicTransportConfiguration: OnboardingSavePublicTransportDto | null;
    highwaysConfiguration: OnboardingSaveHighwaysDto | null;
    parkingConfiguration: OnboardingSaveParkingDto | null;
  }>) => {
    const hasPublicTransportConfiguration = publicTransportConfiguration !== undefined;
    const hasHighwaysConfiguration = highwaysConfiguration !== undefined;
    const hasParkingConfiguration = parkingConfiguration !== undefined;

    const payload: CompanyServicesResponse = {
      parkingConfiguration: hasParkingConfiguration
        ? formatParkingConfiguration(parkingConfiguration)
        : data?.services.parkingConfiguration,
      publicTransportConfiguration: hasPublicTransportConfiguration
        ? formatPublicTransportConfiguration(publicTransportConfiguration)
        : data?.services.publicTransportConfiguration,
      highwaysConfiguration: hasHighwaysConfiguration
        ? formatHighwaysConfiguration(highwaysConfiguration)
        : data?.services.highwaysConfiguration,
    } as CompanyServicesResponse;

    saveCompanyPolicyMutation.mutate(payload);
  };

  return {
    isMutationPending: saveCompanyPolicyMutation.isPending,
    saveCompanyPolicyConfiguration,
    isGeneralConfigLoading,
    lastServiceEnabled,
    availableServices,
    companyPolicyData,
    isQueryLoading,
  };
};
